/* so-cart-optimistic.js — cart quantities: instant figures, never locked (P0-11, 2026-09-30; from the Delivery
   session's Task 26 + Task 27 work). Loaded (deferred) from snippets/cart-drawer.liquid (the drawer is on every
   page) and sections/main-cart.liquid (/cart); the window guard makes a second copy a no-op.

   Our own quantity controller. The theme's cart-items.js (vendor, untouched) debounced ONE change per
   list by 300 ms, sent only the last line changed, then locked the whole list (pointer-events-none, grey line,
   spinner) for the ~1 s /cart/change: no fast taps, nothing else usable while it saved. Here, in the CAPTURE
   phase, the +/− buttons, the quantity box's `change` and the bin are taken over (the theme's handlers for them
   never run, so no debounce and no lock) and fed to one queue:

   - Every line (keyed by its line item key) keeps a DESIRED quantity; each tap moves it (the box's own
     min/max/step apply) and repaints the line at once. Taps never wait and never block anything else.
   - One request in flight at a time: 350 ms after the last tap on any line, EVERY line whose desired quantity
     differs from the server's goes in one batch — one line: /cart/change.js by line key (the theme's endpoint:
     it checks stock); two or more: one /cart/update.js {updates: {key: qty}} — asking for the sections the theme
     renders. Taps on the same line coalesce; taps during a request just move desired and go in the next batch.
     update.js skips the stock check (measured 2026-09-28: 5 of a 3-in-stock "deny" variant, 200), so a line
     asking for more than its box's max (only a typed quantity can) is sent alone through change.js first.
   - The returned section HTML is applied only when the queue is idle (nothing debouncing, nothing in flight,
     every line at its desired quantity), exactly as cart-items.js would, and the same on:line-item:change
     event is dispatched with the same cart JSON (+ sections) — so the bubble, delivery row, add-ons and the
     pre-order gate refresh as before. A response is never rendered while newer taps are pending.
   - Refused (e.g. more than the stock allows): the batch's lines go back to the server's quantity and show the theme's
     message in its own error slot (#line-item-error-N); the rest carries on.
   - Quantity 0 (− or the bin): the line dims to .5 and goes on the idle render.
   - Anything else that re-renders the cart meanwhile (an add-on add, a flavour Change, "Ship N now"): lines
     without pending taps take the server's quantity; lines with pending taps keep them and are resent.
   - Checkout while a change is waiting: the change is sent first, then the click is replayed (the age gate and the
     pre-order trim then run exactly as on a normal click).

   Figures (Task 26): a PLAIN line's price = its shown unit price ("R 319.00 ea", else line ÷ qty) × qty, and
   the checkout total / subtotal move by the same amount. A line showing any discount (a "was" price, a "Saved"
   chip, a discount note) is not guessed: its quantity changes, its figures and the total wait for the server.
   With a cart-level discount code the total isn't guessed. Money keeps the page's own format (only the digits
   of the existing text are replaced). Saving state is light: no grey-out; a small spinner by the line price
   only while that line's price is waiting; aria-busy on the checkout button while anything is unsaved. */
(function () {
  'use strict';

  // ---- Pure (node tests: ux/redesign-2026-09/cart-qty/tests/so-cart-speed.test.cjs) --------------------
  // The first amount in a text: its cents and how it is written. "R 1,286.00 ZAR" → 128600, dec '.', thou ','.
  var RUN = /\d[\d.,'   ]*\d|\d/;
  function parseMoney(text) {
    var m = RUN.exec(String(text == null ? '' : text));
    if (!m) return null;
    var run = m[0], dec = null, decimals = '';
    var d = /([.,])(\d{2})$/.exec(run);
    if (d) { dec = d[1]; decimals = d[2]; run = run.slice(0, -3); }
    var th = /[^\d]/.exec(run);
    var whole = run.replace(/[^\d]/g, '');
    if (!whole) return null;
    return { cents: Number(whole) * 100 + (decimals ? Number(decimals) : 0), start: m.index, end: m.index + m[0].length,
      dec: dec, thou: th ? th[0] : null };
  }
  function formatAmount(cents, like) {
    var c = Math.max(0, Math.round(cents));
    var dec = like && like.dec, thou = like && like.thou;
    if (!thou) thou = dec === ',' ? '.' : ',';
    var whole = String(Math.floor(c / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, thou);
    if (!dec) return c % 100 ? whole + '.' + String(c % 100).padStart(2, '0') : whole;
    return whole + dec + String(c % 100).padStart(2, '0');
  }
  // The same text with its amount replaced by `cents`, written the way the text wrote it; null if it has none.
  function replaceMoney(text, cents) {
    var p = parseMoney(text);
    if (!p) return null;
    return text.slice(0, p.start) + formatAmount(cents, p) + text.slice(p.end);
  }
  // A line's figures at `qty`, from its server-rendered figures `base` {qty, line, unit?, was?, saving?} (cents).
  // unit: the shown per-unit price (the "ea" figure) when the line has one, else line ÷ qty.
  function lineAt(base, qty) {
    if (qty === base.qty) return { line: base.line, was: base.was, saving: base.saving };
    var unit = base.unit != null ? base.unit : Math.round(base.line / base.qty);
    var line = unit * qty;
    var was = base.was != null ? Math.round(base.was / base.qty * qty) : null;
    var saving = was != null ? Math.max(0, was - line)
      : base.saving != null ? Math.round(base.saving / base.qty * qty) : null;
    return { line: line, was: was, saving: saving };
  }
  // The quantity in the box, or null when it isn't a whole number ≥ 0.
  function qtyOf(value) {
    var s = String(value == null ? '' : value).trim();
    if (!/^\d+$/.test(s)) return null;
    return Number(s);
  }
  // Total after the pending lines' changes: rendered total + Σ(new line − rendered line); null when any pending
  // line's change is unknown (a discounted line: the total waits for the server too).
  function totalAfter(baseTotal, deltas) {
    var t = baseTotal;
    for (var i = 0; i < deltas.length; i++) { if (deltas[i] == null) return null; t += deltas[i]; }
    return t;
  }
  // Fix round 1: only a PLAIN line's price is guessed. A line that shows any discount — a struck-through
  // "was" price (compare-at or a line discount), a "Saved" chip, or a discount/allocation note — can change
  // per unit with the quantity (a Dealeasy tier crossed on −, a fixed-amount discount that doesn't multiply),
  // so we never under- or over-state it: its quantity changes, its figures wait (pending) for the server's.
  function guessable(base) {
    return !!base && base.was == null && base.saving == null && !base.discounted;
  }
  // ---- The quantity queue (pure; the browser part below drives it) ----------------------------------------------
  // lines[key] = { server, desired, t }: `server` = the cart's quantity as last known (a render or a response),
  // `desired` = what the shopper asked for, `t` = when it was last touched (the oldest-touched dirty line goes first).
  // P0-11: a request is a BATCH { keys: [...], qty: {key: quantity} } — every dirty line at once, except that a
  // line `solo(key, qty)` flags (asking for more than the box's stock max) goes alone, first, so the server's
  // stock check and its error text apply to it (see send()).
  function createQueue() {
    var lines = {}, inFlight = null, seq = 0;
    function flying(key) { return !!inFlight && Object.prototype.hasOwnProperty.call(inFlight.qty, key); }
    function pendingKey(key) {
      var e = lines[key];
      return !!e && (e.desired !== e.server || flying(key));
    }
    var q = {
      lines: lines,
      inFlight: function () { return inFlight; },
      // A render showed `qty` for `key`. A line without pending taps adopts it; a line with pending taps keeps its
      // desired quantity (it will be sent). Returns true when the screen must show desired instead of `qty`.
      observe: function (key, qty) {
        var e = lines[key];
        if (!e) { lines[key] = { server: qty, desired: qty, t: 0 }; return false; }
        var pend = pendingKey(key);
        e.server = qty;
        if (!pend) e.desired = qty;
        return e.desired !== qty;
      },
      desired: function (key) { return lines[key] ? lines[key].desired : null; },
      set: function (key, qty) {
        var e = lines[key] || (lines[key] = { server: qty, desired: qty, t: 0 });
        e.desired = qty; e.t = ++seq;
      },
      // Lines to send, oldest-touched first. A line in flight counts only if it moved on since it was sent.
      dirty: function () {
        return Object.keys(lines).filter(function (k) {
          var e = lines[k];
          if (flying(k)) return e.desired !== inFlight.qty[k];
          return e.desired !== e.server;
        }).sort(function (a, b) { return lines[a].t - lines[b].t; });
      },
      // The next request (a batch of every dirty line), or null (one in flight at a time).
      next: function (solo) {
        if (inFlight) return null;
        var d = q.dirty();
        if (!d.length) return null;
        var alone = solo ? d.filter(function (k) { return solo(k, lines[k].desired); })[0] : null;
        var keys = alone ? [alone] : d;
        var qty = {};
        keys.forEach(function (k) { qty[k] = lines[k].desired; });
        inFlight = { keys: keys, qty: qty };
        return inFlight;
      },
      // A response (the whole cart): every known line takes the server's quantity (absent = removed = 0).
      done: function (cart) {
        var got = {};
        ((cart && cart.items) || []).forEach(function (it) { got[it.key] = (got[it.key] || 0) + it.quantity; });
        Object.keys(lines).forEach(function (k) { lines[k].server = got[k] || 0; });
        Object.keys(got).forEach(function (k) { if (!lines[k]) lines[k] = { server: got[k], desired: got[k], t: 0 }; });
        // A line the server did not set as asked (clamped to the stock) takes the server's figure, unless it was
        // tapped again since it was sent — else it would be resent for ever.
        if (inFlight) inFlight.keys.forEach(function (k) {
          var e = lines[k];
          if (e && e.server !== inFlight.qty[k] && e.desired === inFlight.qty[k]) e.desired = e.server;
        });
        inFlight = null;
      },
      // Refused: the batch's lines go back to what the server holds (e.g. clamped to the stock). `serverQty`
      // is {key: quantity} (absent = not in the cart = 0), or a number for a one-line batch.
      fail: function (serverQty) {
        var keys = inFlight ? inFlight.keys : [];
        keys.forEach(function (k) {
          var e = lines[k];
          var s = typeof serverQty === 'number' ? serverQty : (serverQty && serverQty[k]) || 0;
          if (e) { e.server = s; e.desired = s; }
        });
        inFlight = null;
      },
      // A fresh read of the whole cart while nothing of ours is in flight: every line not touched after `since`
      // (a tap seq, see stamp()) takes the cart's quantity as both known and wanted; lines gone from the cart are 0.
      adopt: function (cart, since) {
        var got = {};
        ((cart && cart.items) || []).forEach(function (it) { got[it.key] = (got[it.key] || 0) + it.quantity; });
        Object.keys(lines).forEach(function (k) {
          var e = lines[k];
          e.server = got[k] || 0;
          if (!flying(k) && e.t <= since) e.desired = e.server;
        });
        Object.keys(got).forEach(function (k) { if (!lines[k]) lines[k] = { server: got[k], desired: got[k], t: 0 }; });
      },
      stamp: function () { return seq; },
      idle: function () { return !inFlight && q.dirty().length === 0; },
      // Lines no longer on the page (and not in flight) are forgotten.
      prune: function (keys) {
        Object.keys(lines).forEach(function (k) { if (keys.indexOf(k) === -1 && !flying(k)) delete lines[k]; });
      }
    };
    return q;
  }

  // Bin fix: do the lines shown ({key: qty}) match a cart JSON exactly (same keys, same quantities)?
  function sameLines(shown, cart) {
    var got = {};
    ((cart && cart.items) || []).forEach(function (it) { got[it.key] = (got[it.key] || 0) + it.quantity; });
    var a = Object.keys(shown), b = Object.keys(got);
    return a.length === b.length && a.every(function (k) { return got[k] === shown[k]; });
  }
  var pure = { parseMoney: parseMoney, formatAmount: formatAmount, replaceMoney: replaceMoney, lineAt: lineAt, qtyOf: qtyOf,
    totalAfter: totalAfter, guessable: guessable, createQueue: createQueue, sameLines: sameLines, DEBOUNCE_MS: 350 };
  if (typeof module === 'object' && module.exports) module.exports = pure;
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (window.__soCartOptimistic) return;
  window.__soCartOptimistic = true;

  var DEBOUNCE_MS = pure.DEBOUNCE_MS;
  var st = document.createElement('style');
  st.textContent =
    '.so-opt-removing{opacity:.5;transition:opacity .15s}' +
    '.so-q-wait .cart-item__price-now::after{content:"";display:inline-block;width:.75em;height:.75em;margin-left:6px;' +
      'vertical-align:-1px;border:2px solid currentColor;border-right-color:transparent;border-radius:50%;opacity:.45;' +
      'animation:so-q-spin .7s linear infinite}' +
    // Checkout tapped while a change is saving: the same small spinner on the button (no grey-out, no lock).
    '[name="checkout"].so-q-flush{cursor:progress}' +
    '[name="checkout"].so-q-flush::after{content:"";display:inline-block;width:.8em;height:.8em;margin-left:8px;' +
      'vertical-align:-1px;border:2px solid currentColor;border-right-color:transparent;border-radius:50%;opacity:.6;' +
      'animation:so-q-spin .7s linear infinite}' +
    '@keyframes so-q-spin{to{transform:rotate(360deg)}}' +
    '@media (prefers-reduced-motion:reduce){.so-q-wait .cart-item__price-now::after,[name="checkout"].so-q-flush::after{animation-duration:2s}}';
  document.head.appendChild(st);

  // ---- figures (Task 26) --------------------------------------------------------------------------------------
  function moneyNode(el) {
    if (!el || el.querySelector('sup')) return null;   // superscript-cents markup: not safe to rewrite
    var w = document.createTreeWalker(el, NodeFilter.SHOW_TEXT), n;
    while ((n = w.nextNode())) { if (parseMoney(n.nodeValue)) return n; }
    return null;
  }
  function cents(node) { var p = node && parseMoney(node.nodeValue); return p ? p.cents : null; }
  function paint(node, orig, value) { if (!node) return; var t = replaceMoney(orig, value); if (t != null && node.nodeValue !== t) node.nodeValue = t; }

  // The row's rendered figures (its base), read once per rendered row.
  function rowState(row) {
    if (row.__soOpt) return row.__soOpt;
    var input = row.querySelector('input[name="updates[]"]');
    var q0 = input && qtyOf(input.getAttribute('data-initial-value'));
    var now = moneyNode(row.querySelector('.cart-item__price-now'));
    if (!q0 || !now) return null;
    var was = moneyNode(row.querySelector('.cart-item__price .price__was'));
    var ea = moneyNode(row.querySelector('.cart-item__ea'));
    var sav = moneyNode(row.querySelector('.cart-item__saving'));
    var disc = !!row.querySelector('.cart-item__discount, .cart-item__discounts, .discounts, [data-discount-allocation]');
    row.__soOpt = {
      nodes: { now: now, was: was, saving: sav },
      orig: { now: now.nodeValue, was: was && was.nodeValue, saving: sav && sav.nodeValue },
      base: { qty: q0, line: cents(now), unit: ea ? cents(ea) : null, was: was ? cents(was) : null, saving: sav ? cents(sav) : null, discounted: disc },
      delta: 0
    };
    return row.__soOpt;
  }
  function restoreFigures(s) { ['now', 'was', 'saving'].forEach(function (k) { if (s.nodes[k] && s.orig[k] != null) s.nodes[k].nodeValue = s.orig[k]; }); }

  function scopeOf(el) { return el.closest('cart-drawer') || document.querySelector('.cc-main-cart') || document; }
  function totalEls(scope) {
    if (scope.tagName === 'CART-DRAWER') return Array.prototype.slice.call(scope.querySelectorAll('.cart-drawer__summary [name="checkout"], .cart-drawer__summary a.btn--primary'));
    var sum = document.getElementById('cart-summary');
    if (!sum) return [];
    var out = Array.prototype.slice.call(sum.querySelectorAll('[name="checkout"]'));
    sum.querySelectorAll('.flex.justify-between > p.h6').forEach(function (p) { out.push(p); });
    return out;
  }
  function hasCodeDiscount(scope) {
    return scope.tagName === 'CART-DRAWER' ? !!scope.querySelector('.so-cart-discounts li')
      : !!(document.getElementById('cart-summary') && document.getElementById('cart-summary').querySelector('.discount'));
  }
  // Per scope: the rendered totals' text nodes. Rebuilt whenever the footer was re-rendered.
  function totState(scope) {
    var t = scope.__soOptTot;
    if (t && t.nodes.length && t.nodes.every(function (x) { return x.node.isConnected; })) return t;
    t = { nodes: [] };
    totalEls(scope).forEach(function (el) {
      var n = moneyNode(el);
      if (n) t.nodes.push({ node: n, orig: n.nodeValue, base: cents(n), el: el });
    });
    scope.__soOptTot = t;
    return t;
  }
  function paintTotals(scope) {
    var t = totState(scope);
    var rows = Array.prototype.filter.call(scope.querySelectorAll('.cart-item'), function (r) { return r.__soOpt; });
    var deltas = rows.map(function (r) { return r.__soOpt.delta; });
    var guess = !hasCodeDiscount(scope);
    t.nodes.forEach(function (x) {
      if (x.node.__soOrig != null) return;   // so-preorder-cart's "Remove" preview owns this figure right now
      var total = guess && x.base != null ? totalAfter(x.base, deltas) : null;
      if (total != null) paint(x.node, x.orig, total);
      else if (x.node.isConnected) x.node.nodeValue = x.orig;   // not guessed: the rendered figure (pending)
    });
  }
  // Show `qty` on a row: the box, the line's figures, the totals.
  function paintRow(row, qty) {
    var input = row.querySelector('input[name="updates[]"]');
    if (input && input.value !== String(qty)) input.value = String(qty);
    // A "deny" line rendered at its stock has + disabled; after − it can go up again (to the box's max).
    var max = input && qtyOf(input.getAttribute('max'));
    var plus = max != null && row.querySelector('quantity-input button[name="plus"]');
    if (plus) plus.disabled = qty >= max;
    var s = rowState(row);
    if (!s) return;
    row.classList.toggle('so-opt-removing', qty === 0);
    if (qty === 0 || qty === s.base.qty) { restoreFigures(s); s.delta = 0; }
    else if (!guessable(s.base)) { restoreFigures(s); s.delta = null; }   // discounted: figures wait for the server
    else {
      var m = lineAt(s.base, qty);
      paint(s.nodes.now, s.orig.now, m.line);
      if (s.nodes.was && m.was != null) paint(s.nodes.was, s.orig.was, m.was);
      if (s.nodes.saving && m.saving != null) paint(s.nodes.saving, s.orig.saving, m.saving);
      s.delta = m.line - s.base.line;
    }
    if (qty === 0) s.delta = null;   // removal: the total waits for the server (just dim)
    paintTotals(scopeOf(row));
  }

  // ---- rows and keys ------------------------------------------------------------------------------------------
  // The line item key: the per-line data span every row carries (cart-items.liquid), else the bin link's id=.
  function rowKey(row) {
    var d = row.querySelector('.so-preorder-line-data');
    var k = d && d.getAttribute('data-so-line-key');
    if (k) return k;
    var a = row.querySelector('.js-remove-item[href]');
    var m = a && /[?&]id=([^&]+)/.exec(a.getAttribute('href'));
    return m ? decodeURIComponent(m[1]) : null;
  }
  function allRows() { return Array.prototype.slice.call(document.querySelectorAll('cart-items .cart-item')); }
  function rowsOf(key) { return allRows().filter(function (r) { return rowKey(r) === key; }); }
  function initialQty(row) { var i = row.querySelector('input[name="updates[]"]'); return i ? qtyOf(i.getAttribute('data-initial-value')) : null; }

  // ---- controller --------------------------------------------------------------------------------------------
  var q = createQueue();
  var timer = null;
  var lastItems = null;      // the <cart-items> the shopper is using (its sections are the ones requested)
  var lastCart = null;       // the newest response not rendered yet
  var lastCartGen = -1;
  var foreignGen = 0;        // bumps whenever something else re-renders the cart lines
  var needRefresh = false;   // the cart changed in a way no kept response shows (a refused/clamped change)
  var knownCart = null;      // the newest cart JSON we hold (for the event after such a refresh)
  var sent = {};             // keys sent since the last render (their price waits: spinner)
  var errors = {};           // key → the theme's error text, re-shown after renders until the line is tapped again
  var waiters = [];          // checkout waiting for the queue to empty
  var passCheckout = false;

  function busy() { return !!timer || !q.idle() || !!lastCart || needRefresh; }
  function marks() {
    var fl = q.inFlight();
    allRows().forEach(function (row) {
      var k = rowKey(row), s = row.__soOpt;
      var pending = !!k && q.lines[k] && (q.lines[k].desired !== (s ? s.base.qty : q.lines[k].server) || sent[k]);
      var wait = !!pending && ((fl && Object.prototype.hasOwnProperty.call(fl.qty, k)) || !!sent[k] || !!(s && !guessable(s.base)));
      row.classList.toggle('so-q-wait', wait && q.desired(k) !== 0);
      if (pending) row.setAttribute('aria-busy', 'true'); else row.removeAttribute('aria-busy');
    });
    var b = busy();
    document.querySelectorAll('cart-drawer [name="checkout"], #cart-summary [name="checkout"]').forEach(function (btn) {
      if (b) btn.setAttribute('aria-busy', 'true'); else btn.removeAttribute('aria-busy');
      btn.classList.toggle('so-q-flush', waiters.length > 0);   // a checkout tap is waiting for the save
    });
  }
  function showErrors() {
    Object.keys(errors).forEach(function (k) {
      rowsOf(k).forEach(function (row) {
        var el = row.querySelector('.cart-item__error');
        if (el) { el.textContent = errors[k]; el.hidden = false; }
      });
    });
  }
  function clearError(key, row) {
    delete errors[key];
    var el = row && row.querySelector('.cart-item__error');
    if (el) { el.textContent = ''; el.hidden = true; }
  }

  function schedule(ms) {
    clearTimeout(timer);
    timer = setTimeout(function () { timer = null; pump(); }, ms == null ? DEBOUNCE_MS : ms);
    marks();
  }
  // A line asking for more than its box's max (the stock of a "deny" variant, as rendered) goes alone through
  // /cart/change.js, whose stock check refuses it with the theme's own message. Taps can't get there (the box's
  // max stops them); a typed quantity can.
  function overMax(key, qty) {
    return rowsOf(key).some(function (row) {
      var i = row.querySelector('input[name="updates[]"]');
      var max = i && qtyOf(i.getAttribute('max'));
      return max != null && qty > max;
    });
  }
  function pump() {
    if (q.inFlight()) return;
    var job = q.next(overMax);
    if (job) { send(job); return; }
    if (!timer && q.idle()) settle();
  }
  // What an idle render replaces: {el, section, selector}. The theme's own list (cart-items.js
  // getSectionsToRender) is used for everything except the line lists, with two review fixes (P0-11 review #1):
  // the drawer is on /cart too, so the theme's list is the DRAWER's one there — its "cart-items" target is
  // whichever #cart-items comes first and the /cart summary (#cart-summary) is never re-rendered, leaving the
  // page total and "Checkout · R…" on the page-load figures. Here each <cart-items> is filled from its OWN
  // section (the drawer's from the drawer section, the page's from the main-cart section), and #cart-summary
  // from its section.
  function targets(items) {
    var out = [];
    var vendor = [];
    try { if (items && typeof items.getSectionsToRender === 'function') vendor = items.getSectionsToRender(); } catch (e) { /* not upgraded */ }
    vendor.forEach(function (s) {
      if (s.id === 'cart-items' || s.id === 'cart-summary') return;
      var sectionEl = document.getElementById(s.id);
      if (sectionEl) out.push({ el: sectionEl.querySelector(s.selector) || sectionEl, section: s.section, selector: s.selector });
    });
    Array.prototype.forEach.call(document.querySelectorAll('cart-items'), function (ci) {
      var d = ci.closest('cart-drawer'), sec = d && d.closest('.shopify-section');
      var sid = d ? (sec && sec.id.replace('shopify-section-', '')) : ci.getAttribute('data-section');
      if (sid) out.push({ el: ci, section: sid, selector: 'cart-items' });
    });
    var sum = document.getElementById('cart-summary');
    if (sum && sum.getAttribute('data-section')) out.push({ el: sum.querySelector('.cart__summary') || sum, section: sum.getAttribute('data-section'), selector: '.cart__summary' });
    return out;
  }
  function sectionsFor(items) {
    var seen = [];
    targets(items).forEach(function (t) { if (seen.indexOf(t.section) === -1) seen.push(t.section); });
    return seen.slice(0, 5);   // the Section Rendering API's limit
  }
  // The theme's own error text, the way cart-items.js picks it: the response's `errors` when it is text, else
  // the theme's generic cart error.
  function cartError(data) {
    var generic = (window.theme && theme.strings && theme.strings.cartError) || 'Something went wrong.';
    if (data && typeof data.errors === 'string' && data.errors) return data.errors;
    if (data && typeof data.description === 'string' && data.description) return data.description;
    return generic;
  }
  // One line: /cart/change.js by line key (the endpoint the theme uses; it checks stock and clamps). Several lines:
  // ONE /cart/update.js {updates: {key: qty, …}}. Both return the whole cart plus the sections the theme renders.
  function send(job) {
    var items = lastItems && lastItems.isConnected ? lastItems : document.querySelector('cart-drawer cart-items, cart-items');
    var gen = foreignGen;
    job.keys.forEach(function (k) { sent[k] = true; });
    marks();
    var one = job.keys.length === 1;
    var body = one ? { id: job.keys[0], quantity: job.qty[job.keys[0]] } : { updates: job.qty };
    body.sections = sectionsFor(items);
    body.sections_url = window.location.pathname;
    fetch(one ? '/cart/change.js' : '/cart/update.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (res) {
      return res.json().catch(function () { return {}; }).then(function (data) { return { ok: res.ok, data: data }; });
    }).then(function (r) {
      if (r.ok && r.data && Array.isArray(r.data.items)) {
        q.done(r.data);
        lastCart = r.data; lastCartGen = gen; knownCart = r.data;
        return;
      }
      // Refused: the theme's own message; the batch's lines go back to what the cart really holds.
      var msg = cartError(r.data);
      job.keys.forEach(function (k) { errors[k] = msg; });
      return readCart().then(function (c) {
        var held = {};
        (c.items || []).forEach(function (i) { held[i.key] = (held[i.key] || 0) + i.quantity; });
        q.fail(held);
        q.done(c);           // every line: the real cart
        lastCart = null; needRefresh = true; knownCart = c;
      });
    }).catch(function () {
      var held = {};
      job.keys.forEach(function (k) { var e = q.lines[k]; held[k] = e ? e.server : 0; errors[k] = cartError(null); });
      q.fail(held);
      lastCart = null; needRefresh = true;
    }).then(function () {
      // Lines refused/reverted show their server quantity at once.
      Object.keys(errors).forEach(function (k) { var d = q.desired(k); if (d != null) rowsOf(k).forEach(function (row) { paintRow(row, d); }); });
      showErrors();
      if (!timer) pump(); else marks();
    });
  }

  // Idle: nothing debouncing, in flight or pending. Render the newest response the way cart-items.js does.
  function settle() {
    var items = lastItems && lastItems.isConnected ? lastItems : document.querySelector('cart-drawer cart-items, cart-items');
    var data = lastCart, fresh = data && lastCartGen === foreignGen && data.sections;
    lastCart = null;
    var done;
    if (fresh && items) done = render(items, data);
    else if ((data || needRefresh) && items) {
      // Something else re-rendered the cart meanwhile (its HTML is newer than our response), or a change was
      // refused: re-read the cart AND its sections now (review #2: the event must carry the cart as it is now,
      // not our older response — e.g. an add-on added mid-save), and render them like a response.
      done = refreshAll(items);
    } else {
      // Nothing was sent (e.g. + then − back): the screen already shows the rendered figures.
      allRows().forEach(function (row) { if (row.__soOpt) paintRow(row, row.__soOpt.base.qty); });
    }
    needRefresh = false;
    if (fresh) sent = {};
    marks();
    // Review #3: checkout waits until the page shows the final cart (the /cart form posts its updates[] by position).
    Promise.resolve(done).catch(function () {}).then(function () {
      if (busy()) return;                // new taps meanwhile: the waiters stay for the next idle render
      var w = waiters; waiters = [];
      w.forEach(function (f) { f(); });
    });
  }
  function refreshAll(items) {
    var secs = sectionsFor(items);
    var since = q.stamp();
    return Promise.all([
      readCart(),
      // Bin fix: never a cacheable GET. The section URL is the same every time and Shopify answers it with a
      // "page_cache" ETag and no Cache-Control, so a browser may revalidate and reuse an OLD body (a removed line
      // coming back). no-store + a unique query make every read a fresh render.
      fetch(window.location.pathname + '?sections=' + encodeURIComponent(secs.join(',')) + '&so_t=' + Date.now(),
        { cache: 'no-store', headers: { Accept: 'application/json' } }).then(function (r) { return r.ok ? r.json() : {}; })
    ]).then(function (res) {
      var c = res[0];
      if (!c || !Array.isArray(c.items)) throw new Error('no cart');
      var d = {}; Object.keys(c).forEach(function (k) { d[k] = c[k]; });
      d.sections = res[1] || {};
      q.adopt(c, since);                 // lines not tapped since the read began take the cart's figure
      knownCart = c;
      sent = {};
      var r = render(items, d);
      if (!q.idle()) {                   // tapped while we read: keep showing those taps, and send them
        allRows().forEach(function (row) { var k = rowKey(row), want = k && q.desired(k); if (want != null && want !== initialQty(row)) paintRow(row, want); });
        if (!timer) schedule();
      }
      return r;
    }).catch(function () {
      // Fall back to the theme's own refresh.
      return Promise.resolve(items.refresh && items.refresh()).then(function () { sent = {}; showErrors(); marks(); });
    });
  }

  // cart-items.js's own post-response work (vendor file untouched; its element's methods are called where it
  // exposes them). Returns a promise (settled once the page shows `data`).
  function render(items, data) {
    var Ctor = customElements.get('cart-items');
    var tg = targets(items);
    var missing = tg.some(function (t) { return !(data.sections && data.sections[t.section]); });
    if (!Ctor || !tg.length || missing) {
      return Promise.resolve(items.refresh && items.refresh()).then(function () { resetTotals(); showErrors(); announce(items, data); verifySoon(); });
    }
    var drawer = items.closest('cart-drawer');
    var content = drawer && drawer.querySelector('.drawer__content');
    var scrollC = content ? content.scrollTop : 0, scrollD = drawer ? drawer.scrollTop : 0;
    var count = data.item_count;
    var act = document.activeElement, focusKey = null, focusName = null;
    if (act && act.closest && act.closest('cart-items')) { var fr = act.closest('.cart-item'); focusKey = fr && rowKey(fr); focusName = act.getAttribute('name'); }

    if (drawer) {
      if (content) content.classList.toggle('drawer__content--flex', count === 0);
      if (count === 0) { var rec = drawer.querySelector('product-recommendations'); if (rec) rec.remove(); }
    } else if (count === 0) {
      var main = items.closest('.cc-main-cart');
      var title = main && main.querySelector('.js-cart-title');
      if (title) title.style.textAlign = 'center';
      var css = document.getElementById('cart-summary-css'); if (css) css.remove();
      var cc = document.getElementById('cart-summary-and-blocks'); if (cc) cc.hidden = true;
    }
    tg.forEach(function (t) {
      if (!t.el.isConnected) return;     // replaced by an earlier target (e.g. a list inside a re-rendered block)
      t.el.innerHTML = Ctor.getElementHTML(data.sections[t.section], t.selector);
    });
    if (drawer && count === 0 && content) {
      content.classList.add('grow', 'flex', 'items-center');
      if (drawer.querySelector('promoted-products')) content.classList.toggle('drawer__empty-with-promotions', true);
    }
    mo.takeRecords();   // our own render is not "something else re-rendered the cart"
    resetTotals();
    try { items.updateRecommendations(count > 0 ? data.items[0].product_id : null); } catch (e) { /* optional */ }
    try { items.updateLiveRegions(); } catch (e) { /* optional */ }
    document.querySelectorAll('cart-items').forEach(function (ci) { ci.dataset.empty = count === 0; ci.currentTotalItemCount = count; });
    if (content) { content.scrollTop = scrollC; drawer.scrollTop = scrollD; requestAnimationFrame(function () { content.scrollTop = scrollC; drawer.scrollTop = scrollD; }); }
    if (focusKey && focusName) {
      var nr = rowsOf(focusKey).filter(function (r) { return items.contains(r); })[0];
      var f = nr && nr.querySelector('[name="' + focusName + '"]');
      if (f) try { f.focus({ preventScroll: true }); } catch (e) { /* ignore */ }
    }
    showErrors();
    announce(items, data);
    return Promise.resolve();
  }
  // Review #1: the totals' base figures are read again from the freshly rendered footer / summary.
  function resetTotals() {
    document.__soOptTot = null;
    document.querySelectorAll('cart-drawer, .cc-main-cart').forEach(function (s) { s.__soOptTot = null; });
  }
  // The theme's change event, with the cart the page now shows (sections included when we have them).
  function announce(items, data) {
    if (!data || !Array.isArray(data.items)) return;
    var host = items.isConnected ? items : document.querySelector('cart-drawer cart-items, cart-items');
    if (!host) return;
    var first = data.items[0];
    host.dispatchEvent(new CustomEvent('on:line-item:change', {
      bubbles: true,
      detail: { cart: data, variantId: first ? first.variant_id : null, oldQuantity: null, newQuantity: first ? first.quantity : 0 }
    }));
  }

  // Something else re-rendered cart lines (an add, a Change swap, "Ship N now", the theme's refresh): lines
  // without pending taps take the server's quantity; lines with pending taps keep theirs on screen and are resent.
  function readCart() {
    return fetch('/cart.js?so_t=' + Date.now(), { cache: 'no-store', headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); });
  }
  // What the lists show, per line key (the first row of each key; the drawer and /cart show the same lines).
  function shownLines() {
    var out = {};
    allRows().forEach(function (row) {
      if (row.classList.contains('so-opt-removing')) return;
      var k = rowKey(row), n = initialQty(row);
      if (k && n != null && !(k in out)) out[k] = n;
    });
    return out;
  }
  function sameShown(a, b) {   // two shownLines() snapshots: the page did not change while we read the cart
    var ka = Object.keys(a), kb = Object.keys(b);
    return ka.length === kb.length && ka.every(function (k) { return a[k] === b[k]; });
  }
  // Bin fix (Charl, iPhone 2026-09-30): after something ELSE re-renders the lines, check them against the cart once
  // the queue is idle, and re-render from a fresh read when they differ. A late or stale render (a removed line
  // shown again) can then never stay on screen, so what is shown is what the cart holds.
  var verifyTimer = null, verifyTries = 0;
  function verifySoon() {
    clearTimeout(verifyTimer);
    verifyTimer = setTimeout(function () {
      verifyTimer = null;
      if (busy()) { verifySoon(); return; }   // our own idle render will follow
      var shown = shownLines();
      readCart().then(function (c) {
        if (window.__soQ) window.__soQ.log.push({ at: Date.now(), busy: busy(), shown: shown, now: shownLines(), cart: c && c.items && c.items.map(function (i) { return [i.key, i.quantity]; }) });
        if (busy() || !c || !Array.isArray(c.items)) return;
        var items = lastItems && lastItems.isConnected ? lastItems : document.querySelector('cart-drawer cart-items, cart-items');
        if (sameLines(shownLines(), c)) { verifyTries = 0; return; }
        if (sameShown(shown, shownLines()) && items && verifyTries < 2) { verifyTries++; refreshAll(items); }
      }).catch(function () {});
    }, 500);
  }

  function adoptRender() {
    foreignGen++;
    verifySoon();
    var keys = [];
    allRows().forEach(function (row) {
      var k = rowKey(row), qty = initialQty(row);
      if (!k || qty == null) return;
      if (keys.indexOf(k) === -1) { keys.push(k); if (q.observe(k, qty)) paintRow(row, q.desired(k)); }
      else if (q.desired(k) !== qty) paintRow(row, q.desired(k));
    });
    q.prune(keys);
    showErrors();
    if (!q.inFlight() && !timer && q.dirty().length) schedule();
    else marks();
  }
  var mo = new MutationObserver(function (ms) {
    for (var i = 0; i < ms.length; i++) {
      var add = ms[i].addedNodes;
      for (var j = 0; j < add.length; j++) {
        var n = add[j];
        if (n.nodeType === 1 && (n.matches('.cart-item, cart-items') || n.querySelector('.cart-item'))) { adoptRender(); return; }
      }
    }
  });
  mo.observe(document.documentElement, { childList: true, subtree: true });
  // Test hook (tests/theme/p011-fixes.mjs sets window.__soQDebug before load): read-only state for diagnosis.
  if (window.__soQDebug) window.__soQ = { log: [], lines: q.lines, state: function () {
    return { busy: busy(), timer: !!timer, inFlight: q.inFlight(), lastCart: !!lastCart, needRefresh: needRefresh, waiters: waiters.length, verifyTries: verifyTries, verifyTimer: !!verifyTimer, foreignGen: foreignGen };
  } };

  // ---- input: capture phase, before quantity-input / cart-items see it --------------------------------------------
  function take(row, items) {
    var key = rowKey(row);
    if (!key) return null;
    lastItems = items;
    if (!q.lines[key]) q.observe(key, initialQty(row));
    return key;
  }
  function setQty(row, key, qty) {
    q.set(key, qty);
    clearError(key, row);
    rowsOf(key).forEach(function (r) { paintRow(r, qty); });
    schedule();
  }
  document.addEventListener('click', function (e) {
    var t = e.target && e.target.closest ? e.target : null;
    if (!t) return;
    var btn = t.closest('cart-items quantity-input button[name="plus"], cart-items quantity-input button[name="minus"]');
    var bin = !btn && t.closest('cart-items .js-remove-item');
    if (!btn && !bin) return;
    var row = (btn || bin).closest('.cart-item'), items = row && row.closest('cart-items');
    var key = row && take(row, items);
    if (!key) return;                               // unknown row: leave it to the theme
    e.preventDefault(); e.stopImmediatePropagation();
    if (bin) { setQty(row, key, 0); return; }
    var input = row.querySelector('input[name="updates[]"]');
    var cur = q.desired(key);
    input.value = String(cur);
    try { if (btn.name === 'plus') input.stepUp(); else input.stepDown(); } catch (err) { /* not a number input */ }
    var next = qtyOf(input.value);
    if (next == null || next === cur) { input.value = String(cur); return; }   // at the box's min/max
    setQty(row, key, next);
  }, true);
  document.addEventListener('change', function (e) {
    var input = e.target;
    if (!input || !input.matches || !input.matches('cart-items input[name="updates[]"]')) return;
    var row = input.closest('.cart-item'), items = row && row.closest('cart-items');
    var key = row && take(row, items);
    if (!key) return;
    e.stopImmediatePropagation();
    // A typed quantity above the stock is NOT clamped here: like the theme, it is sent (alone, see overMax) and the
    // server's refusal reverts it with the theme's message.
    var qty = qtyOf(input.value);
    if (qty == null) { input.value = String(q.desired(key)); return; }
    setQty(row, key, qty);
  }, true);
  // Checkout with a change still waiting: send it now, then let the checkout go ahead. On WINDOW capture, so it
  // runs before every document-level checkout handler (the age gate, the pre-order trim): nothing sees the click
  // until the cart holds the final quantities. The replay is a fresh click, so the age gate intercepts it exactly
  // as it does a shopper's click (and replays it itself, which then reaches so-preorder-cart as before). With
  // nothing waiting this listener does nothing.
  window.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest && e.target.closest('[name="checkout"]');
    if (!btn || passCheckout) return;
    // Review #4: a second tap while the first is still flushing is swallowed (one replay only).
    if (waiters.length) { e.preventDefault(); e.stopImmediatePropagation(); return; }
    if (!busy()) return;
    e.preventDefault(); e.stopImmediatePropagation();
    clearTimeout(timer); timer = null;
    var inDrawer = !!btn.closest('cart-drawer');
    var went = false;
    var waiter = function () { clearTimeout(guard); go(); };
    var go = function () {
      if (went) return;
      went = true;
      waiters = waiters.filter(function (f) { return f !== waiter; });
      marks();                                         // drops the flushing state
      // The idle render replaced the footer: click the checkout button that is on the page now.
      var b = btn.isConnected ? btn : document.querySelector(inDrawer ? 'cart-drawer [name="checkout"]' : '#cart-summary [name="checkout"]');
      if (!b) return;
      passCheckout = true;
      try { b.click(); } finally { passCheckout = false; }
    };
    var guard = setTimeout(go, 8000);                 // never strand the shopper: Shopify's checkout reads the server cart
    waiters.push(waiter);
    marks();                                           // visible "finishing your changes" state on the button
    pump();
  }, true);
})();
